import type { ActionTree, Module, MutationTree } from 'vuex'
import { AccountService } from '@/services/AccountService'
import {
  createBaseActions,
  createBaseMutations,
  createBaseState,
} from '@/store/modules/Base.module'
import {
  ImpersonatableUsersRequest,
  ImpersonateRequest,
  LoginRequest,
  RequestPasswordRequest,
  ResetPasswordRequest,
  SetCompanyRequest,
  User,
} from '@/classes/generated-models'
import { UserService } from '@/services/User.service'
import { IBaseModuleState } from '@/store/interfaces/IBase.module'
import type { IState } from '@/store'
import router from '@/router'
import { LocalStorageService } from '@/localStorageService'
import { IImpersonatableUser, ICompany, PageNames } from '@/classes/types'
import { IResponseException } from '@/types'

export interface IAccountState extends IBaseModuleState<User> {
  currentUser: User | undefined
  impersonated: boolean
  impersonatedWithOriginalRights: boolean
  impersonatableUsers: IImpersonatableUser[]
  impersonatableCompanies: ICompany[]
}

const accountService = new AccountService()
const userService = new UserService()

const state = (): IAccountState => ({
  ...createBaseState<User, User>(accountService),
  currentUser: undefined,
  impersonated: false,
  impersonatedWithOriginalRights: false,
  impersonatableUsers: [],
  impersonatableCompanies: [],
})

const mutations: MutationTree<IAccountState> = {
  ...createBaseMutations<User, User>(),
  setCurrentUser(state, user: User) {
    state.currentUser = user

    if (user) {
      state.impersonated = user.isImpersonatedByUserName !== null
      state.impersonatedWithOriginalRights = user.isImpersonatedOriginalRights === true
    } else {
      state.impersonated = false
      state.impersonatedWithOriginalRights = false
    }
  },
  setImpersonatableUsers(state, users: IImpersonatableUser[]) {
    state.impersonatableUsers = users
      .map(user => ({
        ...user,
        fullName: `${user.firstName.trim()} ${user.lastName.trim()}`,
        displayName: `${user.firstName.trim()} ${user.lastName.trim()} - ${user.roleName.trim()}`,
      }))
      .sort((a, b) => a.fullName!.localeCompare(b.fullName!))
  },
  setImpersonatableCompanies(state, companies: ICompany[]) {
    state.impersonatableCompanies = companies.sort((a, b) => a.name.localeCompare(b.name))
  },
}

const actions: ActionTree<IAccountState, IState> = {
  ...createBaseActions<User, User>(),

  async login({ commit }, entity: LoginRequest): Promise<User> {
    try {
      commit('setLoading', true)
      const response = await accountService.post(entity, 'login')
      LocalStorageService.setToken(response.token)
      commit('setCurrentUser', response)
      return response
    } catch (error) {
      commit('setResponseException', error as IResponseException)
      throw error
    } finally {
      commit('setLoading', false)
    }
  },

  async fetchCurrentUser({ commit }) {
    try {
      commit('setLoading', true)
      const user = await userService.getCurrentUser()
      commit('setCurrentUser', user)
    } catch (error) {
      console.error(error)
    } finally {
      commit('setLoading', false)
    }
  },

  async fetchImpersonatableUsers({ commit }, request: ImpersonatableUsersRequest) {
    try {
      commit('setLoading', true)
      const users = await userService.getImpersonatableUsers(request)
      commit('setImpersonatableUsers', users)
    } catch (error) {
      console.error(error)
    } finally {
      commit('setLoading', false)
    }
  },

  async fetchImpersonatableCompanies({ commit }) {
    try {
      commit('setLoading', true)
      const companies = await userService.getImpersonatableCompanies()
      commit('setImpersonatableCompanies', companies)
    } catch (error) {
      console.error(error)
    } finally {
      commit('setLoading', false)
    }
  },

  async stopImpersonate({ commit }) {
    try {
      commit('setLoading', true)
      const response = await accountService.post(null, 'stop-impersonating')
      LocalStorageService.setToken(response.token)
      commit('setCurrentUser', response)
      await router.push({ name: PageNames.Impersonate })
    } finally {
      commit('setLoading', false)
    }
  },

  async logout({ commit }) {
    try {
      commit('setLoading', true)
      LocalStorageService.clearToken()
      window.location.href = '/login/auth'
    } finally {
      commit('setLoading', false)
    }
  },

  async confirmPassword({ commit }, request: ResetPasswordRequest): Promise<User> {
    try {
      commit('setLoading', true)
      const response = await accountService.post(request, 'reset-password')
      LocalStorageService.setToken(response.token)
      commit('setCurrentUser', response)
      return response
    } catch (error) {
      commit('setResponseException', error as IResponseException)
      throw error
    } finally {
      commit('setLoading', false)
    }
  },

  async requestPassword({ commit }, request: RequestPasswordRequest): Promise<User> {
    try {
      commit('setLoading', true)
      return await accountService.post(request, 'request-password')
    } catch (error) {
      commit('setResponseException', error as IResponseException)
      throw error
    } finally {
      commit('setLoading', false)
    }
  },

  async startImpersonate({ commit }, request: ImpersonateRequest): Promise<User> {
    try {
      commit('setLoading', true)
      const response = await accountService.post(request, 'impersonate')
      LocalStorageService.setToken(response.token)
      commit('setCurrentUser', response)
      return response
    } finally {
      commit('setLoading', false)
    }
  },

  async setCompany({ commit }, request: SetCompanyRequest): Promise<User> {
    try {
      commit('setLoading', true)
      const response = await accountService.post(request, 'set-company')
      LocalStorageService.setToken(response.token)
      commit('setCurrentUser', response)
      return response
    } finally {
      commit('setLoading', false)
    }
  },
}

export const Account: Module<IAccountState, IState> = {
  namespaced: true,
  state,
  mutations,
  actions,
}
